import React from 'react';
import { Modal, Popover } from 'react-bootstrap';
import { IconAlertTriangle, IconArrowNarrowRight, IconBan, IconCheck, IconClockExclamation, IconInfoCircle } from '@tabler/icons-react';
import { PriceAdjustmentData } from '../../../services/dynamic-pricing-price-ajustment/response/PriceAdjustmentResponse';
import { formatPrice } from '../../../shared/utils/formatPrice';

/**
 * Penjelasan "kenapa rate DPM jadi segini" untuk baris Dynamic Pricing di
 * Monthly Setup.
 *
 * Latar belakang: rate hasil dynamic pricing sering terlihat "tidak sesuai"
 * bagi user karena ada beberapa logic pembatas di backend yang tidak kelihatan
 * di grid — min/max rate override, last-minute restriction, period include/
 * exclude, dan tidak adanya rule yang cocok. Semua batasan itu sekarang dikirim
 * API lewat `dynamic_pricing_info` dan dirangkum di sini.
 *
 * Kasus paling membingungkan yang ditangani di sini: min/max rate override
 * TETAP dijepitkan backend walaupun tidak ada rule yang cocok. Artinya rate di
 * baris DPM bisa berbeda dari rate induk padahal `is_dynamic_pricing` = false —
 * sebelumnya sel seperti ini tidak punya tooltip sama sekali.
 */

type Tone = 'applied' | 'capped' | 'skipped' | 'unknown';

interface LimitRow {
    label: string;
    value: string;
    /** true = batasan ini benar-benar berpengaruh pada tanggal ini. */
    active: boolean;
    note?: string;
}

export interface DpmExplanation {
    tone: Tone;
    statusLabel: string;
    /**
     * Latar badge. Badge SELALU memakai teks putih, jadi warnanya dipilih yang
     * cukup gelap supaya lolos WCAG AA (>=4.5:1):
     *   warning #f76707 → putih hanya 3.04:1, dipakai #bf4f04 (4.84:1)
     *   success #2fb344 → putih hanya 2.74:1, dipakai #248232 (4.87:1)
     *   secondary #667382 sudah 4.84:1, dipakai apa adanya
     *
     * Warna ditulis eksplisit (bukan class `bg-warning`) karena Tabler mengeset
     * `.badge { color: var(--tblr-secondary) }` sementara react-bootstrap
     * `bg="warning"` hanya mengatur background — kombinasi itu menghasilkan
     * teks abu-abu di atas oranye yang nyaris tak terbaca.
     */
    badgeBg: string;
    /** Warna aksen tone untuk border callout & highlight. */
    toneColor: string;
    /** Penjelasan utama dalam satu kalimat. */
    reason: string;
    limits: LimitRow[];
    /** Rate dasar sebelum dynamic pricing. */
    originalRate: string;
    /** Rate setelah rule, sebelum dijepit override (null kalau tidak relevan). */
    rateBeforeOverride: string | null;
    finalRate: string;
    ruleLabel: string | null;
    basisLabel: string;
    basisValue: string;
    /** true kalau rate final berbeda dari rate asli. */
    rateChanged: boolean;
}

/** formatPrice melempar error untuk nilai non-numerik — bungkus supaya aman. */
const safePrice = (value: string | number | null | undefined, fallback = '-'): string => {
    if (value === null || value === undefined || value === '') return fallback;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return fallback;
    return formatPrice(num);
};

const REASON_TEXT: Record<string, string> = {
    rule_applied: 'A dynamic pricing rule matched this date and was applied.',
    no_matching_rule: 'No pricing rule covers the current value, so the base rate was kept.',
    last_minute_restriction: 'Skipped — this date falls inside the last-minute restriction window.',
    excluded_period: 'Skipped — this date is inside a period marked as "exclude".',
    not_in_included_period: 'Skipped — this date is not covered by any "include" period.',
    no_pricing_item: 'Skipped — this room type / rate plan is not part of the active dynamic pricing group.',
    no_dynamic_pricing_group: 'This property has no active dynamic pricing group.',
};

/**
 * Merangkum satu sel DPM menjadi penjelasan siap-tampil.
 * Dipakai bersama oleh popover (hover) dan modal (klik).
 */
export const describeDynamicPricing = (data: PriceAdjustmentData): DpmExplanation => {
    const info = data.dynamic_pricing_info;

    const minApplied = Boolean(info?.min_rate_override_applied);
    const maxApplied = Boolean(info?.max_rate_override_applied);
    const isCapped = minApplied || maxApplied;

    let tone: Tone = 'unknown';
    let statusLabel = 'No adjustment';
    let variant = 'secondary';

    if (isCapped) {
        tone = 'capped';
        // Sengaja pendek: di popover selebar 260px, label panjang mendorong judul
        // "Dynamic Pricing" jadi dua baris. Kalimat lengkapnya ada di `reason`.
        statusLabel = maxApplied ? 'Capped by max' : 'Raised to min';
        variant = 'warning';
    } else if (data.is_dynamic_pricing) {
        tone = 'applied';
        statusLabel = 'Rule applied';
        variant = 'success';
    } else if (info?.reason_code) {
        tone = 'skipped';
        statusLabel = 'Not applied';
        variant = 'secondary';
    }

    // Lihat komentar `badgeBg` di DpmExplanation: badge memakai versi warna yang
    // lebih gelap supaya teks putih tetap terbaca, sementara `toneColor` tetap
    // memakai warna Tabler asli agar aksen di modal/popover senada dengan warna
    // sel DPM di grid.
    const BADGE_BG: Record<string, string> = {
        warning: '#bf4f04',
        success: '#248232',
        secondary: 'var(--tblr-secondary)',
    };
    const badgeBg = BADGE_BG[variant];
    const toneColor = `var(--tblr-${variant})`;

    // Prioritaskan penjelasan clamp, karena itulah yang paling sering bikin
    // angka terlihat "salah" di mata user.
    let reason: string;
    if (maxApplied) {
        reason = 'The calculated rate exceeded the maximum rate override, so it was capped down to that limit.';
    } else if (minApplied) {
        reason = 'The calculated rate was below the minimum rate override, so it was raised up to that limit.';
    } else {
        reason = (info?.reason_code && REASON_TEXT[info.reason_code])
            || data.adjustment_message
            || 'No additional information available.';
    }

    const isAvailabilityBased = info?.calculation_method === 'availability_based';
    const basisLabel = isAvailabilityBased ? 'Availability' : 'Occupancy';
    const basisValue = isAvailabilityBased ? `${data.available ?? 0} room(s)` : `${data.occupancy ?? 0}%`;

    const ruleLabel = data.rule_adjustment_percentage
        ? `${data.rule_start ?? 0}–${data.rule_end ?? 0}${isAvailabilityBased ? '' : '%'} → ×${data.rule_adjustment_percentage}`
        : null;

    const limits: LimitRow[] = [];

    if (info?.min_rate_override !== null && info?.min_rate_override !== undefined) {
        limits.push({
            label: 'Min rate',
            value: safePrice(info.min_rate_override),
            active: minApplied,
            note: minApplied ? 'Raised the final rate.' : 'Not reached.',
        });
    }
    if (info?.max_rate_override !== null && info?.max_rate_override !== undefined) {
        limits.push({
            label: 'Max rate',
            value: safePrice(info.max_rate_override),
            active: maxApplied,
            note: maxApplied ? 'Capped the final rate.' : 'Not reached.',
        });
    }
    if (info?.last_minute_restriction) {
        const blocked = info.reason_code === 'last_minute_restriction';
        limits.push({
            label: 'Last-minute',
            value: `${info.last_minute_days ?? 0} day(s)`,
            active: blocked,
            note: info.last_minute_cutoff_date
                ? `Applies from ${info.last_minute_cutoff_date}.`
                : undefined,
        });
    }
    if (info?.min_stay_restriction) {
        const enforced = Boolean(info.item_min_stay_restriction);
        limits.push({
            label: 'Min stay',
            value: `${info.min_stay_nights ?? 1} night(s)`,
            active: enforced,
            note: enforced
                ? 'Applied to MIN / MIN-T rows.'
                : 'Enabled on the group, but not this rate plan.',
        });
    }

    const originalRate = safePrice(data.original_rate);
    const finalRate = safePrice(data.rate);
    const beforeOverrideRaw = info?.rate_before_override;
    // Hanya tampilkan langkah "sebelum override" kalau memang berbeda dari hasil akhir.
    const rateBeforeOverride = isCapped && beforeOverrideRaw ? safePrice(beforeOverrideRaw) : null;

    return {
        tone,
        statusLabel,
        badgeBg,
        toneColor,
        reason,
        limits,
        originalRate,
        rateBeforeOverride,
        finalRate,
        ruleLabel,
        basisLabel,
        basisValue,
        rateChanged: originalRate !== finalRate,
    };
};

/** Ikon status kecil untuk dipakai di badge/heading. */
const ToneIcon: React.FC<{ tone: Tone; size?: number }> = ({ tone, size = 14 }) => {
    if (tone === 'applied') return <IconCheck size={size} />;
    if (tone === 'capped') return <IconAlertTriangle size={size} />;
    if (tone === 'skipped') return <IconBan size={size} />;
    return <IconInfoCircle size={size} />;
};

/** Badge status: selalu teks putih di atas latar yang sudah dicek kontrasnya. */
const StatusBadge: React.FC<{ x: DpmExplanation; size?: number }> = ({ x, size = 12 }) => (
    <span
        className="badge text-white d-inline-flex align-items-center gap-1"
        style={{ backgroundColor: x.badgeBg }}
    >
        <ToneIcon tone={x.tone} size={size} />
        {x.statusLabel}
    </span>
);

/**
 * Judul seksi kartu. Dibuat kecil + uppercase (bukan `h4`/`card-title` bawaan)
 * supaya muat satu baris walau kolomnya sempit, dan tidak bersaing perhatian
 * dengan angka rate yang justru harus menonjol.
 */
const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="card-header py-2">
        <span
            className="text-secondary fw-bold text-uppercase"
            style={{ fontSize: '11px', letterSpacing: '.04em' }}
        >
            {children}
        </span>
    </div>
);

/**
 * Callout alasan. Sengaja TIDAK memakai `alert alert-light` — Tabler mengeset
 * `--tblr-alert-color: var(--tblr-light-text-emphasis)` untuk varian itu
 * sehingga teksnya nyaris tak terbaca di atas latar terang. Di sini latarnya
 * netral, teks memakai warna body normal, dan tone-nya cukup diwakili garis
 * aksen di kiri.
 */
const ReasonCallout: React.FC<{ x: DpmExplanation }> = ({ x }) => (
    <div
        className="d-flex gap-2 align-items-start rounded border bg-white p-3"
        style={{ borderLeft: `4px solid ${x.toneColor}` }}
    >
        <span className="flex-shrink-0" style={{ color: x.toneColor, marginTop: 1 }}>
            <ToneIcon tone={x.tone} size={16} />
        </span>
        <div className="text-body fs-4">{x.reason}</div>
    </div>
);

/** Alur perhitungan rate: dasar → hasil rule → dijepit override → final. */
const RateTrail: React.FC<{ x: DpmExplanation }> = ({ x }) => (
    <div className="d-flex align-items-center flex-wrap gap-1">
        <span className="text-secondary text-decoration-line-through">{x.originalRate}</span>
        {x.rateBeforeOverride && (
            <>
                <IconArrowNarrowRight size={12} className="text-secondary" />
                <span className="text-secondary">{x.rateBeforeOverride}</span>
            </>
        )}
        <IconArrowNarrowRight size={12} className="text-secondary" />
        <span className="fw-bold" style={{ color: x.toneColor, fontSize: '13px' }}>{x.finalRate}</span>
    </div>
);

/**
 * `compact` dipakai di popover (hover): font lebih kecil & tanpa garis pemisah
 * supaya isinya ringkas. Modal memakai versi normal yang lebih lega + catatan.
 */
const LimitList: React.FC<{ limits: LimitRow[]; showNotes?: boolean; compact?: boolean }> = ({ limits, showNotes, compact }) => {
    if (limits.length === 0) {
        return (
            <div className={`text-secondary fst-italic ${compact ? '' : 'fs-5'}`} style={compact ? { fontSize: '11px' } : undefined}>
                No extra limits configured.
            </div>
        );
    }

    const labelSize = compact ? { fontSize: '11.5px' } : undefined;

    return (
        <ul className="list-unstyled mb-0">
            {limits.map((limit, i) => (
                <li
                    key={limit.label}
                    className={`d-flex justify-content-between align-items-start gap-2 ${i > 0 ? (compact ? 'mt-1' : 'mt-2 pt-2 border-top') : ''}`}
                >
                    <span
                        className={`${compact ? '' : 'fs-4'} ${limit.active ? 'fw-semibold' : 'text-secondary'}`}
                        style={{ ...labelSize, ...(limit.active ? { color: 'var(--tblr-warning)' } : {}) }}
                    >
                        {limit.active && <IconAlertTriangle size={compact ? 11 : 13} className="me-1" />}
                        {limit.label}
                        {showNotes && limit.note && (
                            <span className="d-block text-secondary fw-normal fs-5 lh-sm mt-1">{limit.note}</span>
                        )}
                    </span>
                    <span
                        className={`text-end text-nowrap ${compact ? '' : 'fs-4'} ${limit.active ? 'fw-bold' : 'fw-semibold'}`}
                        style={labelSize}
                    >
                        {limit.value}
                    </span>
                </li>
            ))}
        </ul>
    );
};

/**
 * Popover ringkas saat hover sel DPM.
 * Dipanggil sebagai fungsi (bukan komponen) karena prop `overlay` milik
 * OverlayTrigger react-bootstrap butuh element, mengikuti pola lama di file ini.
 */
export const renderPriceDetailPopover = (data: PriceAdjustmentData) => {
    if (!data) {
        return (
            <Popover id="popover-price-detail-empty">
                <Popover.Body>No price details available.</Popover.Body>
            </Popover>
        );
    }

    const x = describeDynamicPricing(data);

    return (
        <Popover id={`popover-price-detail-${data.id || data.date}`} style={{ maxWidth: 260 }}>
            <Popover.Header as="div" className="d-flex align-items-center justify-content-between gap-2 py-1 px-2">
                <span className="fw-bold text-nowrap" style={{ fontSize: '12px' }}>Dynamic Pricing</span>
                <StatusBadge x={x} size={10} />
            </Popover.Header>
            <Popover.Body className="p-2" style={{ fontSize: '11.5px' }}>
                <RateTrail x={x} />

                <div className="d-flex justify-content-between gap-2 mt-1">
                    <span className="text-secondary">{x.basisLabel}</span>
                    <span className="fw-bold text-nowrap">{x.basisValue}</span>
                </div>
                <div className="d-flex justify-content-between gap-2">
                    <span className="text-secondary">Rule</span>
                    <span className="fw-bold text-nowrap">{x.ruleLabel ?? 'None matched'}</span>
                </div>

                <div className="text-body mt-2 pt-2 border-top lh-sm">{x.reason}</div>

                {x.limits.length > 0 && (
                    <div className="mt-2 pt-2 border-top">
                        <div
                            className="text-secondary fw-bold text-uppercase mb-1"
                            style={{ fontSize: '10px', letterSpacing: '.04em' }}
                        >
                            Limits
                        </div>
                        <LimitList limits={x.limits} compact />
                    </div>
                )}

                <div className="text-secondary mt-2" style={{ fontSize: '10px' }}>
                    Click the cell for the full breakdown.
                </div>
            </Popover.Body>
        </Popover>
    );
};

interface DynamicPricingDetailModalProps {
    show: boolean;
    onClose: () => void;
    data: PriceAdjustmentData | null;
    /** Nama rate plan, untuk konteks di judul modal. */
    ratePlanName?: string;
}

/** Modal detail lengkap saat sel DPM diklik. */
export const DynamicPricingDetailModal: React.FC<DynamicPricingDetailModalProps> = ({ show, onClose, data, ratePlanName }) => {
    if (!data) return null;

    const x = describeDynamicPricing(data);
    const info = data.dynamic_pricing_info;

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="py-3">
                {/* Judul berdiri sendiri di baris pertama; badge + konteks turun ke
                    baris kedua supaya header tidak penuh berdesakan seperti sebelumnya. */}
                <Modal.Title as="div">
                    <div className="fs-2 fw-bold lh-sm">Dynamic Pricing Details</div>
                    <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                        <StatusBadge x={x} />
                        <span className="text-secondary fs-5">
                            {ratePlanName ? `${ratePlanName} · ` : ''}{data.date}
                        </span>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
                <div className="mb-3">
                    <ReasonCallout x={x} />
                </div>

                {/* Tinggi kartu dibiarkan mengikuti isinya (BUKAN h-100): jumlah
                    baris limit berbeda-beda per rate plan, jadi h-100 justru
                    menyisakan ruang kosong besar di bawah kartu yang lebih pendek. */}
                <div className="row g-3">
                    <div className="col-lg-7">
                        <div className="card">
                            <SectionHeader>Rate breakdown</SectionHeader>
                            <div className="card-body p-0">
                                <table className="table card-table table-sm table-vcenter mb-0">
                                    {/* Lebar kolom dikunci: tanpa ini kolom label melar
                                        mengikuti teks terpanjang dan nilai rupiah di kanan
                                        ikut terhimpit sampai wrap jadi dua baris. */}
                                    <colgroup>
                                        <col />
                                        <col style={{ width: '42%' }} />
                                    </colgroup>
                                    <tbody>
                                        <tr>
                                            <td className="text-secondary fs-4">Base rate</td>
                                            <td className="text-end fw-bold fs-4 text-nowrap">{x.originalRate}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-secondary fs-4">{x.basisLabel}</td>
                                            <td className="text-end fw-bold fs-4 text-nowrap">{x.basisValue}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-secondary fs-4">Matched rule</td>
                                            <td className="text-end fw-bold fs-4 text-nowrap">{x.ruleLabel ?? 'None matched'}</td>
                                        </tr>
                                        {x.rateBeforeOverride && (
                                            <tr>
                                                <td className="text-secondary fs-4">Rate before limits</td>
                                                <td className="text-end fw-bold fs-4 text-nowrap text-decoration-line-through text-secondary">
                                                    {x.rateBeforeOverride}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ borderTop: `2px solid ${x.toneColor}` }}>
                                            <td className="fw-bold fs-4">Final rate</td>
                                            <td
                                                className="text-end fw-bold fs-2 text-nowrap"
                                                style={{ color: x.toneColor }}
                                            >
                                                {x.finalRate}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            {!x.rateChanged && (
                                <div className="card-footer py-2 fs-5 text-secondary">
                                    Final rate is identical to the base rate — nothing changed for this date.
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-lg-5">
                        <div className="card">
                            {/* Judul dipendekkan dari "Limits from the pricing group":
                                di kolom sempit judul panjang selalu wrap dua baris. */}
                            <SectionHeader>Pricing group limits</SectionHeader>
                            <div className="card-body py-2">
                                <LimitList limits={x.limits} showNotes />
                            </div>
                            {(info?.calculation_method || info?.last_minute_cutoff_date) && (
                                <div className="card-footer py-2 fs-5 text-secondary">
                                    {info?.calculation_method && (
                                        <div>
                                            Method: <span className="fw-semibold">{info.calculation_method.replace('_', ' ')}</span>
                                        </div>
                                    )}
                                    {info?.last_minute_restriction && info?.last_minute_cutoff_date && (
                                        <div className="d-flex align-items-start gap-2 mt-1">
                                            <IconClockExclamation size={13} className="flex-shrink-0 mt-1" />
                                            <span>
                                                Dates before <span className="fw-semibold">{info.last_minute_cutoff_date}</span> are
                                                never adjusted.
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};
